import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getIcon } from "@/lib/iconMapping";
import { SEOHead } from "@/components/SEOHead";

type PlanFilter = 'all' | 'basic' | 'standard';

interface FeatureDetail {
  id: string;
  detail_text: string;
  display_order: number;
}

interface Feature {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  plan: string;
  show_both_badges: boolean;
  display_order: number;
  feature_details: FeatureDetail[];
}

interface FeatureCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  display_order: number;
  features: Feature[];
}

interface ComparisonFeature {
  id: string;
  name: string;
  basic: boolean;
  standard: boolean;
  display_order: number;
}

export default function Features() {
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');

  // Fetch feature categories with features and details
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['feature-categories'],
    queryFn: async () => {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('feature_categories')
        .select('*')
        .order('display_order');

      if (categoriesError) throw categoriesError;

      const { data: featuresData, error: featuresError } = await supabase
        .from('features')
        .select('*, feature_details(*)')
        .order('display_order');

      if (featuresError) throw featuresError;

      // Group features by category
      const categoriesWithFeatures: FeatureCategory[] = categoriesData.map(category => ({
        ...category,
        features: featuresData
          .filter(f => f.category_id === category.id)
          .map(f => ({
            ...f,
            feature_details: (f.feature_details || []).sort((a: FeatureDetail, b: FeatureDetail) => a.display_order - b.display_order)
          }))
      }));

      return categoriesWithFeatures;
    }
  });

  // Fetch comparison features
  const { data: comparisonFeatures = [], isLoading: comparisonLoading } = useQuery({
    queryKey: ['comparison-features'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comparison_features')
        .select('*')
        .order('display_order');

      if (error) throw error;
      return data as ComparisonFeature[];
    }
  });

  // Categories that should show BOTH plan badges (dashboards, administration, financial)
  const showBothBadgesCategories = ['Core Features', 'Administration', 'Financial Tools'];

  const filteredCategories = categories.map(category => ({
    ...category,
    features: category.features.filter(feature => {
      if (planFilter === 'all') return true;
      if (planFilter === 'basic') return feature.plan === 'basic' || feature.name === 'PTA Admin Dashboard';
      // Standard includes all features except 'Basic Events' (show advanced events instead)
      if (planFilter === 'standard') return feature.name !== 'Basic Events';
      return true;
    })
  })).filter(category => category.features.length > 0);

  const getPlanBadgeStyles = (plan: 'basic' | 'standard') => {
    if (plan === 'basic') {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800';
    }
    return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800';
  };

  if (categoriesLoading || comparisonLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 space-y-16 animate-fade-in">
      <SEOHead slug="features" />
      {/* Plan Filter Section */}
      <section className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-6">Filter by Plan</h2>
        <Tabs value={planFilter} onValueChange={(v) => setPlanFilter(v as PlanFilter)} className="w-full max-w-md mx-auto">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all" className="gap-2">
              All Features
            </TabsTrigger>
            <TabsTrigger value="basic" className="gap-2">
              <span className="hidden sm:inline">Basic</span>
              <Badge variant="outline" className="ml-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-0 text-xs">
                £99/yr
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="standard" className="gap-2">
              <span className="hidden sm:inline">Standard</span>
              <Badge variant="outline" className="ml-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0 text-xs">
                £129/yr
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-sm text-muted-foreground mt-4">
          {planFilter === 'all' && 'Showing all 30+ features across both plans'}
          {planFilter === 'basic' && 'Showing 14 features included in Basic Plan'}
          {planFilter === 'standard' && 'Showing all 30+ features included in Standard Plan (includes everything in Basic)'}
        </p>
      </section>

      {/* Feature Categories */}
      <section className="space-y-12">
        {filteredCategories.map((category) => {
          const CategoryIcon = getIcon(category.icon);
          return (
            <div key={category.id} className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <CategoryIcon className="w-6 h-6 text-black dark:text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{category.name}</h2>
                  <p className="text-muted-foreground">{category.description}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.features.map((feature) => {
                  const FeatureIcon = getIcon(feature.icon);
                  const showBothBadges = planFilter === 'all' && (
                    showBothBadgesCategories.includes(category.name) || 
                    feature.show_both_badges
                  );
                  const showSingleBadge = planFilter === 'all' && !showBothBadges;
                  return (
                    <Card key={feature.id} className="group hover:shadow-elevated transition-all duration-300 border-border/50 hover:border-primary/30">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                              <FeatureIcon className="w-5 h-5 text-black dark:text-white transition-colors" />
                            </div>
                            <CardTitle className="text-lg">{feature.name}</CardTitle>
                          </div>
                          {showBothBadges && (
                            <div className="flex gap-1 shrink-0">
                              <Badge variant="outline" className={`text-xs ${getPlanBadgeStyles('basic')}`}>
                                Basic
                              </Badge>
                              <Badge variant="outline" className={`text-xs ${getPlanBadgeStyles('standard')}`}>
                                Standard
                              </Badge>
                            </div>
                          )}
                          {showSingleBadge && (
                            <Badge variant="outline" className={`text-xs shrink-0 ${getPlanBadgeStyles(feature.plan as 'basic' | 'standard')}`}>
                              {feature.plan === 'basic' ? 'Basic' : 'Standard'}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-3">{feature.description}</CardDescription>
                        {feature.feature_details.length > 0 && (
                          <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="details" className="border-0">
                              <AccordionTrigger className="text-sm text-primary hover:no-underline py-2">
                                Learn more
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-1.5 text-sm text-muted-foreground">
                                  {feature.feature_details.map((detail) => (
                                    <li key={detail.id} className="flex items-start gap-2">
                                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                      {detail.detail_text}
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Comparison Table */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground mb-2">Compare Plans</h2>
          <p className="text-muted-foreground">See exactly what's included in each plan</p>
        </div>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[300px] font-semibold">Feature</TableHead>
                  <TableHead className="text-center w-[150px]">
                    <div className="flex flex-col items-center gap-1 pb-2">
                      <span className="font-semibold">Basic</span>
                      <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-0">
                        £99.99/year
                      </Badge>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[150px]">
                    <div className="flex flex-col items-center gap-1 pb-2">
                      <span className="font-semibold">Standard</span>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0">
                        £129.99/year
                      </Badge>
                      
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow key={feature.id} className={index % 2 === 0 ? 'bg-muted/20' : ''}>
                    <TableCell className="font-medium">{feature.name}</TableCell>
                    <TableCell className="text-center">
                      {feature.basic ? (
                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {feature.standard ? (
                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>

      {/* Bottom padding */}
      <div className="pb-16" />

    </div>
  );
}
